import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Switch, TextInput, Linking, Platform } from 'react-native';
import { useRouter, useLocalSearchParams, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useOwnerScope } from '../../../src/context/OwnerScopeContext';
import { getToken } from '../../../src/api/client';
import { useToast } from '../../../src/components/Toast';
import Card from '../../../src/components/Card';
import Input from '../../../src/components/Input';
import Button from '../../../src/components/Button';
import Picker from '../../../src/components/Picker';
import ContentContainer from '../../../src/components/ContentContainer';
import InlineConfirm from '../../../src/components/InlineConfirm';
import InlineMessage from '../../../src/components/InlineMessage';
import EditShiftModal, { ShiftLike } from '../../../src/components/EditShiftModal';
import { AdminToolbar } from '../../../src/components/AdminList';
import { useTheme } from '../../../src/context/ThemeContext';
import { useNetwork } from '../../../src/context/NetworkContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import { titleCase, formatPhone } from '../../../src/utils/format';

interface ShiftRecord {
  id: string;
  locationId: string;
  locationName: string | null;
  clockInTime: string;
  clockOutTime: string | null;
  status: string;
  notes: string | null;
  lockedAt: string | null;
  lockedPeriodLabel: string | null;
}

interface AttendantDetail {
  id: string;
  userId: string;
  userName: string;
  isActive: boolean;
  hourlyRateCents: number | null;
  overtimeRule: string;
  locations: {
    locationId: string;
    locationName: string;
    role: string;
    roleId?: string;
    permissions?: any;
  }[];
  recentShifts?: ShiftRecord[];
}

interface SubordinateListEntry {
  id: string;
  name: string | null;
  attendantManagerId?: string;
}

interface OwnerLocation {
  id: string;
  name: string;
}

interface AttendantRole {
  id: string;
  name: string;
  permissions: Record<string, unknown>;
  sortOrder: number;
}

interface OwnerAttendantBrief {
  id: string;
  userName: string;
}

interface TimecardRow {
  id: string;
  locationId: string | null;
  locationName: string | null;
  clockInTime: string;
  clockOutTime: string | null;
  minutes: number | null;
  status: string;
  notes: string | null;
  lockedAt: string | null;
  lockedPeriodLabel: string | null;
}

interface QrActivityRow {
  code: string;
  templateName: string | null;
  offer: string;
  status: string;
  claimed: boolean;
  recipientEmail: string | null;
  createdAt: string;
  expiresAt: string | null;
}

interface WashActivityRow {
  id: string;
  createdAt: string;
  locationName: string | null;
  bayName: string | null;
  durationMinutes: number | null;
  status: string;
}

interface RefundActivityRow {
  id: string;
  amountCents: number;
  reason: string | null;
  status: string;
  createdAt: string;
  customerName: string | null;
}

/** Format ISO string as owner-local short date + time. */
const formatDateTime = (isoString: string) =>
  new Date(isoString).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

/** AttendantDetailScreen - manage attendant status, PIN, and location assignments. */
export default function AttendantDetailScreen({ entityId }: { entityId?: string } = {}) {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const params = useLocalSearchParams<{ id: string }>();
  const id = entityId ?? params.id;
  const [attendant, setAttendant] = useState<AttendantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Inline confirm/message state
  const [confirmRemoveLocation, setConfirmRemoveLocation] = useState<{ locationId: string; locationName: string } | null>(null);
  const [removingLocation, setRemovingLocation] = useState(false);
  const [confirmDeleteAttendant, setConfirmDeleteAttendant] = useState(false);
  const [inlineMsg, setInlineMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Add location state
  const [ownerLocations, setOwnerLocations] = useState<OwnerLocation[]>([]);
  const [addLocationId, setAddLocationId] = useState('');
  const [addLocationRole, setAddLocationRole] = useState('junior');
  const [addLocationRoleId, setAddLocationRoleId] = useState('');
  const [addingLocation, setAddingLocation] = useState(false);
  const [roles, setRoles] = useState<AttendantRole[]>([]);

  // Pay info
  const [rateInput, setRateInput] = useState('');
  const [savingPay, setSavingPay] = useState(false);

  // Subordinates
  const [allOwnerAttendants, setAllOwnerAttendants] = useState<OwnerAttendantBrief[]>([]);
  const [subordinates, setSubordinates] = useState<SubordinateListEntry[]>([]);
  const [savingSubs, setSavingSubs] = useState(false);

  // Timecard edit modal
  const [editingShift, setEditingShift] = useState<ShiftLike | null>(null);

  // Full activity report (stats, QRs issued, washes started, refunds)
  interface ActivityReport {
    profile: { name: string | null; email: string | null; phone: string | null; accountNumber: string | null; since: string | null };
    stats: { totalShifts: number; totalHours: number; washesStarted: number; qrsIssued: number; qrsRedeemed: number; refundsProcessed: number; refundsTotalCents: number };
    qrsIssued: { code: string; templateName: string | null; offer: string; status: string; claimed: boolean; createdAt: string; expiresAt: string | null }[];
    washesStarted: { createdAt: string; locationName: string | null; bayName: string | null; durationMinutes: number | null; status: string }[];
    refunds: { amountCents: number; reason: string | null; status: string; createdAt: string }[];
  }
  const [report, setReport] = useState<ActivityReport | null>(null);
  const [exporting, setExporting] = useState(false);

  // Paginated activity sections
  const [tcRows, setTcRows] = useState<TimecardRow[]>([]);
  const [tcTotal, setTcTotal] = useState(0);
  const [tcPage, setTcPage] = useState(1);
  const [tcSearch, setTcSearch] = useState('');

  const [qrRows, setQrRows] = useState<QrActivityRow[]>([]);
  const [qrTotal, setQrTotal] = useState(0);
  const [qrPage, setQrPage] = useState(1);
  const [qrSearch, setQrSearch] = useState('');

  const [washRows, setWashRows] = useState<WashActivityRow[]>([]);
  const [washTotal, setWashTotal] = useState(0);
  const [washPage, setWashPage] = useState(1);
  const [washSearch, setWashSearch] = useState('');

  const [refundRows, setRefundRows] = useState<RefundActivityRow[]>([]);
  const [refundTotal, setRefundTotal] = useState(0);
  const [refundPage, setRefundPage] = useState(1);
  const [refundSearch, setRefundSearch] = useState('');

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const { downloadFile } = await import('../../../src/utils/fileExport');
      const apiBase = (process.env.EXPO_PUBLIC_API_URL as string | undefined) || '';
      const path = scope.api.scopePath(`/api/attendants/${id}/report?format=csv`);
      const headers: Record<string, string> = {};
      if (Platform.OS !== 'web') {
        const token = getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      await downloadFile(`${apiBase}${path}`, `attendant-report.csv`, Object.keys(headers).length > 0 ? headers : undefined);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Export failed', message: err?.message });
    } finally { setExporting(false); }
  };

  const router = useRouter();
  const toast = useToast();
  const scope = useOwnerScope();
  const { isOffline } = useNetwork();

  const fetchActivity = async <T,>(section: string, p: number, s: string) => {
    const params = new URLSearchParams({ section, page: String(p), limit: '10' });
    if (s) params.set('search', s);
    return scope.api.get<{ rows: T[]; total: number }>(`/api/attendants/${id}/activity?${params}`);
  };

  const fetchTimecards = async (p = 1, searchOverride?: string) => {
    try {
      const data = await fetchActivity<TimecardRow>('shifts', p, searchOverride ?? tcSearch);
      setTcRows(data.rows); setTcTotal(data.total); setTcPage(p);
    } catch { /* ignore */ }
  };

  const fetchQrs = async (p = 1, searchOverride?: string) => {
    try {
      const data = await fetchActivity<QrActivityRow>('qrs', p, searchOverride ?? qrSearch);
      setQrRows(data.rows); setQrTotal(data.total); setQrPage(p);
    } catch { /* ignore */ }
  };

  const fetchWashes = async (p = 1, searchOverride?: string) => {
    try {
      const data = await fetchActivity<WashActivityRow>('washes', p, searchOverride ?? washSearch);
      setWashRows(data.rows); setWashTotal(data.total); setWashPage(p);
    } catch { /* ignore */ }
  };

  const fetchRefunds = async (p = 1, searchOverride?: string) => {
    try {
      const data = await fetchActivity<RefundActivityRow>('refunds', p, searchOverride ?? refundSearch);
      setRefundRows(data.rows); setRefundTotal(data.total); setRefundPage(p);
    } catch { /* ignore */ }
  };

  const fetchAttendant = async () => {
    let failures = 0;
    const track = <T,>(p: Promise<T>, fallback: T): Promise<T> => p.catch(() => { failures++; return fallback; });
    try {
      const [data, locs, rolesData, allAtts, mgrAssignments] = await Promise.all([
        scope.api.get<AttendantDetail>(`/api/attendants/${id}`),
        scope.api.get<OwnerLocation[]>('/api/locations'),
        track(scope.api.get<AttendantRole[]>('/api/attendant-roles'), [] as AttendantRole[]),
        track(scope.api.get<OwnerAttendantBrief[]>('/api/attendants'), [] as OwnerAttendantBrief[]),
        track(scope.api.get<{ subordinates: SubordinateListEntry[]; managers: SubordinateListEntry[] }>(`/api/attendants/${id}/manager-assignments`), { subordinates: [], managers: [] }),
      ]);
      scope.api.get<ActivityReport>(`/api/attendants/${id}/report`).then(setReport).catch(() => {});
      fetchTimecards(1);
      fetchQrs(1);
      fetchWashes(1);
      fetchRefunds(1);
      setAttendant(data);
      setOwnerLocations(locs);
      setRoles(rolesData);
      setAllOwnerAttendants(allAtts);
      setSubordinates(mgrAssignments.subordinates);
      setRateInput(data.hourlyRateCents !== null && data.hourlyRateCents !== undefined ? (data.hourlyRateCents / 100).toFixed(2) : '');
      if (rolesData.length > 0 && !addLocationRoleId) {
        setAddLocationRoleId(rolesData[0].id);
      }
      if (failures > 0 && !isOffline) {
        toast.show({ type: 'warning', title: 'Some data didn’t load', message: `${failures} section${failures === 1 ? '' : 's'} failed to refresh.` });
      }
    } catch (err: any) {
      if (!isOffline) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  // Determine whether this attendant has manage_team perm at any assigned location
  const hasManageTeam = useMemo(() => {
    if (!attendant) return false;
    const roleById = new Map(roles.map(r => [r.id, r.permissions]));
    return attendant.locations.some(loc => {
      if (!loc.roleId) return false;
      const perms = roleById.get(loc.roleId);
      return !!(perms && (perms as any).manage_team);
    });
  }, [attendant, roles]);

  // Role-based team scope: an assigned role that grants manage_team AND defines
  // manage_team_role_ids replaces the per-person subordinate picker. Legacy roles
  // (key absent) keep the picker.
  const roleScopedTeamRole = useMemo(() => {
    if (!attendant) return null;
    const roleById = new Map(roles.map(r => [r.id, r]));
    for (const loc of attendant.locations) {
      if (!loc.roleId) continue;
      const role = roleById.get(loc.roleId);
      const perms = role?.permissions as any;
      if (perms?.manage_team && Array.isArray(perms.manage_team_role_ids)) return role ?? null;
    }
    return null;
  }, [attendant, roles]);

  const handleSavePay = async () => {
    if (!attendant) return;
    const trimmed = rateInput.trim();
    const cents = trimmed === '' ? null : Math.round(parseFloat(trimmed) * 100);
    if (cents !== null && (!Number.isFinite(cents) || cents < 0 || cents > 10_000_000)) {
      toast.show({ type: 'error', title: 'Invalid rate', message: 'Enter a positive dollar value or leave blank.' });
      return;
    }
    setSavingPay(true);
    try {
      await scope.api.patch(`/api/attendants/${id}`, { hourlyRateCents: cents });
      setAttendant({ ...attendant, hourlyRateCents: cents });
      toast.show({ type: 'success', title: 'Saved', message: 'Hourly rate updated' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSavingPay(false);
    }
  };

  const toggleSubordinate = (attendantId: string) => {
    setSubordinates(prev => {
      if (prev.some(s => s.id === attendantId)) {
        return prev.filter(s => s.id !== attendantId);
      }
      const found = allOwnerAttendants.find(a => a.id === attendantId);
      return [...prev, { id: attendantId, name: found?.userName || null }];
    });
  };

  const handleSaveSubordinates = async () => {
    setSavingSubs(true);
    try {
      await scope.api.post(`/api/attendants/${id}/subordinates`, {
        subordinateAttendantIds: subordinates.map(s => s.id),
      });
      toast.show({ type: 'success', title: 'Saved', message: 'Team updated' });
      fetchAttendant();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSavingSubs(false);
    }
  };

  useFocusEffect(useCallback(() => { fetchAttendant(); }, [id]));

  const handleToggleActive = async (value: boolean) => {
    if (!attendant) return;

    setUpdating(true);
    try {
      await scope.api.patch(`/api/attendants/${id}`, { isActive: value });
      setAttendant({ ...attendant, isActive: value });
      toast.show({
        type: 'success',
        title: 'Updated',
        message: `Attendant ${value ? 'activated' : 'deactivated'}`
      });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveFromLocation = async (locationId: string) => {
    setRemovingLocation(true);
    try {
      await scope.api.delete(`/api/attendants/${id}/locations/${locationId}`);
      setConfirmRemoveLocation(null);
      toast.show({ type: 'success', title: 'Removed', message: 'Attendant removed from location' });
      fetchAttendant();
    } catch (err: any) {
      setConfirmRemoveLocation(null);
      setInlineMsg({ type: 'error', message: err.message });
    } finally {
      setRemovingLocation(false);
    }
  };

  const handleChangeRole = async (locationId: string, roleId: string) => {
    try {
      await scope.api.patch(`/api/attendants/${id}/locations/${locationId}`, { roleId });
      toast.show({ type: 'success', title: 'Updated', message: 'Role updated successfully' });
      fetchAttendant();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const handleDeleteAttendant = async () => {
    setDeleting(true);
    try {
      await scope.api.delete(`/api/attendants/${id}`);
      setConfirmDeleteAttendant(false);
      toast.show({ type: 'success', title: 'Deleted', message: 'Attendant removed' });
      router.back();
    } catch (err: any) {
      setConfirmDeleteAttendant(false);
      setInlineMsg({ type: 'error', message: err.message });
    } finally {
      setDeleting(false);
    }
  };

  const handleAddLocation = async () => {
    if (!addLocationId) {
      toast.show({ type: 'error', title: 'Error', message: 'Select a location' });
      return;
    }

    setAddingLocation(true);
    try {
      await scope.api.post(`/api/attendants/${id}/locations`, {
        locationId: addLocationId,
        role: 'junior',
        roleId: addLocationRoleId || undefined,
      });
      toast.show({ type: 'success', title: 'Added', message: 'Location assigned' });
      setAddLocationId('');
      setAddLocationRole('junior');
      fetchAttendant();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setAddingLocation(false);
    }
  };

  // Locations the attendant is NOT yet assigned to
  const unassignedLocations = ownerLocations.filter(
    loc => !attendant?.locations.some(al => al.locationId === loc.id)
  );

  // Auto-select first unassigned location when list changes
  useEffect(() => {
    if (unassignedLocations.length > 0 && !unassignedLocations.some(l => l.id === addLocationId)) {
      setAddLocationId(unassignedLocations[0].id);
    }
  }, [unassignedLocations.length, attendant?.locations.length]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!attendant) {
    return (
      <View style={styles.errorContainer}>
        <Ionicons name="alert-circle" size={64} color={colors.danger} />
        <Text style={styles.errorText}>Attendant not found</Text>
        <Button title="Go Back" onPress={() => router.back()} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      <ContentContainer>
      {inlineMsg && (
        <InlineMessage
          type={inlineMsg.type}
          message={inlineMsg.message}
          visible={true}
          onDismiss={() => setInlineMsg(null)}
        />
      )}

      {/* Profile Card */}
      <Card>
        <View style={styles.profileHeader}>
          <View style={styles.profileIcon}>
            <Ionicons name="person" size={40} color={colors.primary} />
          </View>
          <View style={styles.profileInfo}>
            <Text style={styles.profileName}>{titleCase(attendant.userName)}</Text>
            {report?.profile.email && (
              <TouchableOpacity onPress={() => Linking.openURL(`mailto:${report.profile.email}`)}>
                <Text style={{ fontSize: fontSize.sm, color: colors.primary, marginTop: 2 }}>{report.profile.email}</Text>
              </TouchableOpacity>
            )}
            {report?.profile.phone && (
              <TouchableOpacity onPress={() => Linking.openURL(`tel:${report.profile.phone}`)}>
                <Text style={{ fontSize: fontSize.sm, color: colors.primary, marginTop: 2 }}>{formatPhone(report.profile.phone)}</Text>
              </TouchableOpacity>
            )}
            {report?.profile.accountNumber && (
              <Text style={{ fontSize: fontSize.xs, color: colors.textLight, marginTop: 2 }}>#{report.profile.accountNumber}{report.profile.since ? ` · since ${new Date(report.profile.since).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}` : ''}</Text>
            )}
          </View>
          <Button
            title={exporting ? '…' : 'CSV'}
            size="sm"
            variant="outline"
            onPress={handleExportCsv}
            disabled={exporting}
            icon={<Ionicons name="download-outline" size={16} color={colors.primary} />}
          />
        </View>

        {report && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.md }}>
            {([
              [String(report.stats.totalShifts), 'Shifts'],
              [`${report.stats.totalHours}h`, 'Hours'],
              [String(report.stats.washesStarted), 'Washes started'],
              [`${report.stats.qrsRedeemed}/${report.stats.qrsIssued}`, 'QRs used/issued'],
              [`$${(report.stats.refundsTotalCents / 100).toFixed(0)}`, `Refunds (${report.stats.refundsProcessed})`],
            ] as const).map(([value, label]) => (
              <View key={label} style={{ flexGrow: 1, minWidth: 100, backgroundColor: colors.background, borderRadius: borderRadius.md, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, alignItems: 'center' }}>
                <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold as any, color: colors.text }}>{value}</Text>
                <Text style={{ fontSize: 10, color: colors.textLight, textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 1 }}>{label}</Text>
              </View>
            ))}
          </View>
        )}

        <View style={styles.activeToggle}>
          <View>
            <Text style={styles.toggleLabel}>Active Status</Text>
            <Text style={styles.toggleDescription}>
              {attendant.isActive ? 'Attendant can clock in/out' : 'Attendant cannot clock in/out'}
            </Text>
          </View>
          <Switch
            value={attendant.isActive}
            onValueChange={handleToggleActive}
            disabled={updating}
            trackColor={{ false: colors.borderLight, true: colors.success + '80' }}
            thumbColor={attendant.isActive ? colors.success : colors.textLight}
          />
        </View>
      </Card>

      {/* Pay Info */}
      <Card style={styles.sectionCard}>
        <Text style={styles.sectionTitle}>Pay Info</Text>
        <Text style={styles.fieldLabel}>Hourly rate (USD)</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
          <Text style={{ fontSize: fontSize.md, color: colors.text }}>$</Text>
          <TextInput
            style={styles.inlineInput}
            value={rateInput}
            onChangeText={setRateInput}
            placeholder="e.g. 15.00"
            keyboardType="decimal-pad"
          />
          <Button
            title="Save"
            onPress={handleSavePay}
            loading={savingPay}
            size="sm"
            style={{ minWidth: 80 }}
          />
        </View>
        <Text style={styles.fieldHint}>
          Used to compute gross pay on payroll PDFs. Leave blank to omit dollar totals.
          Overtime rule: weekly 40+ (federal), 1.5× rate.
        </Text>
      </Card>

      {/* Subordinates (only when this attendant has manage_team perm at some location) */}
      {hasManageTeam && (
        <Card style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Manages Attendants</Text>
          {roleScopedTeamRole ? (
            <Text style={styles.fieldHint}>
              Team scope is set by the {roleScopedTeamRole.name} role settings — this attendant
              manages everyone holding the roles selected there. Edit it under Manage Roles.
            </Text>
          ) : (
            <>
              <Text style={styles.fieldHint}>
                This attendant has the Manage Team permission. Select the attendants they supervise.
                They will be able to view and edit timecards for those attendants only.
              </Text>
              <View style={{ marginTop: spacing.sm }}>
                {allOwnerAttendants.filter(a => a.id !== attendant.id).length === 0 ? (
                  <Text style={styles.emptyText}>No other attendants to assign.</Text>
                ) : (
                  allOwnerAttendants
                    .filter(a => a.id !== attendant.id)
                    .map(a => {
                      const selected = subordinates.some(s => s.id === a.id);
                      return (
                        <TouchableOpacity
                          key={a.id}
                          onPress={() => toggleSubordinate(a.id)}
                          style={[styles.subRow, selected && { backgroundColor: colors.primaryBg }]}
                        >
                          <Ionicons name={selected ? 'checkbox' : 'square-outline'} size={20} color={selected ? colors.primary : colors.textLight} />
                          <Text style={{ fontSize: fontSize.sm, color: colors.text, marginLeft: spacing.sm, flex: 1 }}>{titleCase(a.userName)}</Text>
                        </TouchableOpacity>
                      );
                    })
                )}
                <Button
                  title="Save Team"
                  onPress={handleSaveSubordinates}
                  loading={savingSubs}
                  size="sm"
                  style={{ marginTop: spacing.sm }}
                  icon={<Ionicons name="save" size={16} color={colors.white} />}
                />
              </View>
            </>
          )}
        </Card>
      )}

      {/* Recent timecards */}
      <Card style={styles.sectionCard}>
        <Text style={styles.sectionTitle}>Recent Timecards</Text>
        <AdminToolbar
          search={tcSearch}
          onSearchChange={setTcSearch}
          onSearch={() => fetchTimecards(1)}
          total={tcTotal}
          page={tcPage}
          limit={10}
          onPageChange={(p) => fetchTimecards(p)}
          csvEndpoint={scope.api.scopePath(`/api/attendants/${id}/report`)}
          csvFilename="attendant-report.csv"
          label="timecard"
        />
        {tcRows.length === 0 ? (
          <Text style={styles.emptyText}>No shifts found.</Text>
        ) : (
          tcRows.map(s => {
            const start = new Date(s.clockInTime);
            const end = s.clockOutTime ? new Date(s.clockOutTime) : null;
            const durationMs = end ? end.getTime() - start.getTime() : Date.now() - start.getTime();
            const hours = (durationMs / 3_600_000).toFixed(2);
            return (
              <View key={s.id} style={styles.shiftRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.shiftPrimary}>
                    {formatDateTime(s.clockInTime)} {end ? '→ ' + formatDateTime(s.clockOutTime!) : '(active)'}
                  </Text>
                  <Text style={styles.shiftSecondary}>
                    {s.locationName || '?'} · {hours}h
                    {s.lockedAt && <Text style={{ color: colors.warning }}>  🔒 {s.lockedPeriodLabel || 'locked'}</Text>}
                  </Text>
                </View>
                <Button
                  title="Edit"
                  onPress={() => setEditingShift({
                    id: s.id,
                    attendantName: attendant.userName,
                    locationName: s.locationName,
                    clockInTime: s.clockInTime,
                    clockOutTime: s.clockOutTime,
                    notes: s.notes,
                    status: s.status,
                    lockedAt: s.lockedAt,
                  })}
                  size="sm"
                  variant="outline"
                  icon={<Ionicons name="pencil" size={14} color={colors.primary} />}
                />
              </View>
            );
          })
        )}
      </Card>

      <EditShiftModal
        visible={editingShift !== null}
        shift={editingShift}
        onClose={() => setEditingShift(null)}
        onSaved={fetchAttendant}
      />

      {/* Reward QRs issued */}
      {(qrRows.length > 0 || qrTotal > 0 || qrSearch !== '') && (
        <Card style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Reward QRs Issued ({qrTotal})</Text>
          <AdminToolbar
            search={qrSearch}
            onSearchChange={setQrSearch}
            onSearch={() => fetchQrs(1)}
            total={qrTotal}
            page={qrPage}
            limit={10}
            onPageChange={(p) => fetchQrs(p)}
            csvEndpoint={scope.api.scopePath(`/api/attendants/${id}/report`)}
            csvFilename="attendant-report.csv"
            label="QR"
          />
          {qrRows.length === 0 && <Text style={styles.emptyText}>No reward QRs found</Text>}
          {qrRows.map(q => (
            <View key={q.code} style={styles.shiftRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.shiftPrimary}>{q.templateName || q.offer}</Text>
                <Text style={styles.shiftSecondary}>
                  {q.offer} · code {q.code}{q.recipientEmail ? ` · ${q.recipientEmail}` : ''} · {formatDateTime(q.createdAt)}
                </Text>
              </View>
              <View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4, backgroundColor: (q.claimed ? colors.success : q.status === 'active' ? colors.warning : colors.textLight) + '20' }}>
                <Text style={{ fontSize: 10, fontWeight: '700', color: q.claimed ? colors.success : q.status === 'active' ? colors.warning : colors.textLight }}>
                  {q.claimed ? 'Claimed' : q.status === 'active' ? 'Outstanding' : titleCase(q.status)}
                </Text>
              </View>
            </View>
          ))}
        </Card>
      )}

      {/* Washes started */}
      {(washRows.length > 0 || washTotal > 0 || washSearch !== '') && (
        <Card style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Washes Started ({washTotal})</Text>
          <AdminToolbar
            search={washSearch}
            onSearchChange={setWashSearch}
            onSearch={() => fetchWashes(1)}
            total={washTotal}
            page={washPage}
            limit={10}
            onPageChange={(p) => fetchWashes(p)}
            csvEndpoint={scope.api.scopePath(`/api/attendants/${id}/report`)}
            csvFilename="attendant-report.csv"
            label="wash"
          />
          {washRows.length === 0 && <Text style={styles.emptyText}>No washes found</Text>}
          {washRows.map(w => (
            <View key={w.id} style={styles.shiftRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.shiftPrimary}>{w.locationName || '?'}{w.bayName ? ` · ${w.bayName}` : ''}</Text>
                <Text style={styles.shiftSecondary}>
                  {formatDateTime(w.createdAt)}
                  {w.durationMinutes != null ? ` · ${w.durationMinutes} min` : ''} · {w.status}
                </Text>
              </View>
            </View>
          ))}
        </Card>
      )}

      {/* Refunds processed */}
      {(refundRows.length > 0 || refundTotal > 0 || refundSearch !== '') && (
        <Card style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Refunds Processed ({refundTotal})</Text>
          <AdminToolbar
            search={refundSearch}
            onSearchChange={setRefundSearch}
            onSearch={() => fetchRefunds(1)}
            total={refundTotal}
            page={refundPage}
            limit={10}
            onPageChange={(p) => fetchRefunds(p)}
            csvEndpoint={scope.api.scopePath(`/api/attendants/${id}/report`)}
            csvFilename="attendant-report.csv"
            label="refund"
          />
          {refundRows.length === 0 && <Text style={styles.emptyText}>No refunds found</Text>}
          {refundRows.map(r => (
            <View key={r.id} style={styles.shiftRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.shiftPrimary}>-${(r.amountCents / 100).toFixed(2)}{r.customerName ? ` · ${titleCase(r.customerName)}` : ''}</Text>
                <Text style={styles.shiftSecondary}>
                  {formatDateTime(r.createdAt)} · {r.reason || 'No reason'} · {r.status}
                </Text>
              </View>
            </View>
          ))}
        </Card>
      )}

      {/* Assigned Locations */}
      <Card style={styles.sectionCard}>
        <Text style={styles.sectionTitle}>Assigned Locations</Text>
        {attendant.locations.length === 0 ? (
          <Text style={styles.emptyText}>No locations assigned</Text>
        ) : (
          attendant.locations.map(loc => (
            <View key={loc.locationId} style={styles.locationCard}>
              <View style={styles.locationHeader}>
                <View style={styles.locationIconSmall}>
                  <Ionicons name="location" size={20} color={colors.primary} />
                </View>
                <Text style={styles.locationName}>{loc.locationName}</Text>
              </View>

              <View style={styles.roleSelector}>
                <Text style={styles.roleSelectorLabel}>Role:</Text>
                <Picker
                  options={roles.map(r => ({ label: r.name, value: r.id }))}
                  value={loc.roleId || ''}
                  onValueChange={(value) => handleChangeRole(loc.locationId, value)}
                />
              </View>

              <Button
                title="Remove from Location"
                onPress={() => setConfirmRemoveLocation({ locationId: loc.locationId, locationName: loc.locationName })}
                variant="danger"
                size="sm"
                icon={<Ionicons name="trash" size={16} color={colors.white} />}
                style={styles.removeButton}
              />
              <InlineConfirm
                message={`Remove this attendant from ${loc.locationName}?`}
                visible={confirmRemoveLocation?.locationId === loc.locationId}
                onConfirm={() => handleRemoveFromLocation(loc.locationId)}
                onCancel={() => setConfirmRemoveLocation(null)}
                confirmLabel="Remove"
                loading={removingLocation}
                variant="danger"
              />
            </View>
          ))
        )}

        {/* Add to new location */}
        {unassignedLocations.length > 0 && (
          <View style={styles.addLocationSection}>
            <Text style={styles.addLocationTitle}>Add to Location</Text>
            <Picker
              label="Location"
              options={unassignedLocations.map(loc => ({
                label: loc.name,
                value: loc.id,
              }))}
              value={addLocationId}
              onValueChange={(val: string) => setAddLocationId(val)}
            />
            <Picker
              label="Role"
              options={roles.map(r => ({ label: r.name, value: r.id }))}
              value={addLocationRoleId}
              onValueChange={(val: string) => setAddLocationRoleId(val)}
            />
            <Button
              title="Add Location"
              onPress={handleAddLocation}
              loading={addingLocation}
              disabled={!addLocationId}
              size="sm"
              icon={<Ionicons name="add" size={18} color={colors.white} />}
              style={styles.addLocationButton}
            />
          </View>
        )}
      </Card>

      {/* Delete Attendant */}
      <Card style={styles.dangerCard}>
        <Text style={styles.dangerTitle}>Remove Attendant</Text>
        <Text style={styles.dangerDescription}>
          Permanently remove this attendant. This will delete all location assignments and shift history.
        </Text>
        <Button
          title="Delete Attendant"
          onPress={() => setConfirmDeleteAttendant(true)}
          loading={deleting}
          variant="danger"
          icon={<Ionicons name="trash" size={18} color={colors.white} />}
        />
        <InlineConfirm
          message={`Permanently remove ${titleCase(attendant.userName)} as an attendant? This will delete all location assignments and shift history.`}
          visible={confirmDeleteAttendant}
          onConfirm={handleDeleteAttendant}
          onCancel={() => setConfirmDeleteAttendant(false)}
          confirmLabel="Delete"
          loading={deleting}
          variant="danger"
        />
      </Card>

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: c.background,
  },
  scrollContent: {
    padding: spacing.md,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: c.background,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: c.background,
    padding: spacing.xl,
  },
  errorText: {
    fontSize: fontSize.lg,
    color: c.textSecondary,
    marginTop: spacing.md,
    marginBottom: spacing.xl,
  },
  profileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  profileIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: c.primaryBg,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: fontSize.xl,
    fontWeight: fontWeight.bold,
    color: c.text,
  },
  profileEmail: {
    fontSize: fontSize.md,
    color: c.textSecondary,
    marginTop: spacing.xs,
  },
  profilePhone: {
    fontSize: fontSize.sm,
    color: c.textLight,
    marginTop: 4,
  },
  activeToggle: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: c.borderLight,
  },
  toggleLabel: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
  },
  toggleDescription: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    marginTop: 2,
  },
  sectionCard: {
    marginTop: spacing.md,
  },
  sectionTitle: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.bold,
    color: c.text,
    marginBottom: spacing.md,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.md,
  },
  halfButton: {
    flex: 1,
  },
  emptyText: {
    fontSize: fontSize.sm,
    color: c.textLight,
    textAlign: 'center',
    paddingVertical: spacing.lg,
  },
  fieldLabel: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    color: c.text,
    marginBottom: spacing.xs,
  },
  fieldHint: {
    fontSize: fontSize.xs,
    color: c.textSecondary,
    marginTop: spacing.xs,
  },
  inlineInput: {
    flex: 1,
    backgroundColor: c.surfaceSecondary,
    borderRadius: borderRadius.sm,
    padding: spacing.sm,
    fontSize: fontSize.sm,
    color: c.text,
    borderWidth: 1,
    borderColor: c.border,
  },
  subRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    borderRadius: borderRadius.sm,
    marginBottom: 4,
  },
  shiftRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: c.borderLight,
    gap: spacing.sm,
  },
  shiftPrimary: {
    fontSize: fontSize.sm,
    color: c.text,
  },
  shiftSecondary: {
    fontSize: fontSize.xs,
    color: c.textSecondary,
    marginTop: 2,
  },
  locationCard: {
    padding: spacing.md,
    backgroundColor: c.surface,
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: c.border,
  },
  locationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  locationIconSmall: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: c.primaryBg,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  locationName: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
  },
  roleSelector: {
    marginBottom: spacing.md,
  },
  roleSelectorLabel: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    color: c.textSecondary,
    marginBottom: spacing.xs,
  },
  removeButton: {
    marginTop: spacing.xs,
  },
  addLocationSection: {
    marginTop: spacing.lg,
    paddingTop: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: c.borderLight,
  },
  addLocationTitle: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
    marginBottom: spacing.sm,
  },
  addLocationButton: {
    marginTop: spacing.sm,
  },
  dangerCard: {
    marginTop: spacing.md,
    borderWidth: 1,
    borderColor: c.danger + '40',
  },
  dangerTitle: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.bold,
    color: c.danger,
    marginBottom: spacing.xs,
  },
  dangerDescription: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    marginBottom: spacing.md,
  },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
